// Where a mark goes. The arithmetic every chart in this app shares.
//
// It is a module of its own, with its own tests, because this is the layer that
// fails quietly: a chart drawn against the wrong maximum still looks like a
// chart, and the reader trusts the picture over the figures beside it. Nothing
// here knows what it is drawing, and nothing here reads a colour.

export interface Band {
  readonly index: number;
  readonly x: number;
  readonly width: number;
}

/**
 * `count` bars across `width`, with `gap` of surface between neighbours.
 *
 * The gap is the design's, not a rounding artefact: two fills that touch read as
 * one shape, so bands are separated deliberately and the last one still ends
 * exactly on the right edge.
 */
export function bands(count: number, width: number, gap: number): Band[] {
  if (count <= 0) return [];
  const bandWidth = (width - gap * (count - 1)) / count;
  return Array.from({ length: count }, (_, index) => ({
    index,
    x: index * (bandWidth + gap),
    width: bandWidth,
  }));
}

/** The steps a reader can divide in their head, per power of ten. */
const NICE_STEPS = [1, 2, 2.5, 5];

/** At most this many gridlines above the baseline. */
const MAX_INTERVALS = 5;

/**
 * The gridline step and the axis top for values up to `max`: the smallest round
 * step that reaches `max` in at most five intervals, and the first multiple of
 * it at or above `max`. So $10.4M tops out at $12.5M, not $20M, and the chart
 * uses its height.
 *
 * A maximum of zero answers one rather than zero. Every other function here
 * divides by the top, and an axis whose top is zero is a chart that draws
 * nothing and reports NaN for the position of the baseline. The result is
 * stable: the top of a top is itself.
 */
export function axisScale(max: number): { top: number; step: number } {
  if (!Number.isFinite(max) || max <= 0) return { top: 1, step: 0.25 };
  let exponent = Math.floor(Math.log10(max / MAX_INTERVALS));
  for (;;) {
    const magnitude = 10 ** exponent;
    for (const nice of NICE_STEPS) {
      const step = nice * magnitude;
      const intervals = Math.ceil(max / step - 1e-9);
      if (intervals <= MAX_INTERVALS) return { top: intervals * step, step };
    }
    exponent += 1;
  }
}

/** The top of the value axis. */
export function axisMax(max: number): number {
  return axisScale(max).top;
}

/** Tick values from zero to the axis top, inclusive of both ends, one per gridline. */
export function ticks(max: number): number[] {
  const { top, step } = axisScale(max);
  const count = Math.round(top / step);
  return Array.from({ length: count + 1 }, (_, index) => step * index);
}

/**
 * The y of a value in a plot `height` tall, measured DOWN from its top — the
 * direction SVG counts in, stated once here so no component inverts it again.
 */
export function valueY(value: number, max: number, height: number): number {
  const top = axisMax(max);
  return height - (value / top) * height;
}

/**
 * The x of point `index` of `count` across `width`: the first on the left edge
 * and the last on the right, which is what makes two series of the same length
 * line up. A lone point is centred, because a single month pinned to the left
 * edge reads as the start of a line that is not there.
 */
export function pointX(index: number, count: number, width: number): number {
  if (count <= 1) return width / 2;
  return (width / (count - 1)) * index;
}

/** Roughly how wide one axis character is, in the plot's own units (11px figures). */
const AXIS_CHAR_WIDTH = 6.5;
/** Clear space kept between two neighbouring axis labels. */
const AXIS_LABEL_GAP = 6;

/**
 * Label every `n`th position along an x axis, so no two labels overlap.
 *
 * A month of days fits a label under every bar; a year of weeks does not, and
 * labels drawn on top of each other read as nothing at all. The stride is the
 * fewest positions apart that leaves room for the widest label.
 */
export function tickStride(labels: readonly string[], width: number): number {
  if (labels.length <= 1) return 1;
  const widest = Math.max(...labels.map((label) => label.length));
  const needed = widest * AXIS_CHAR_WIDTH + AXIS_LABEL_GAP;
  return Math.max(1, Math.ceil(needed / (width / labels.length)));
}

/**
 * Whether position `index` of `count` carries its label at `stride`. The last
 * position always does, so the axis says where it ends, and a strided label
 * too close to it gives way rather than running into it.
 */
export function showsTick(index: number, count: number, stride: number): boolean {
  if (index === count - 1) return true;
  return index % stride === 0 && count - 1 - index >= stride;
}
